/**
 * A confirmation for the grid. The confirm button has focus when it opens,
 * so Enter confirms and Escape cancels, and focus goes back to the grid when
 * it closes so the keys carry on from the same cell.
 */

import React, { useRef } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { AlertTriangle, Loader2 } from 'lucide-react';

export const GridConfirm: React.FC<{
  title: string;
  message: string;
  /** A caution under the question, set apart with a warning icon. */
  warning?: string;
  confirmLabel: string;
  /** The request is running: both buttons wait, and the dialog stays open. */
  loading?: boolean;
  onAccept: () => void;
  onCancel: () => void;
  /** Where focus goes when it closes. */
  onCloseAutoFocus: (event: Event) => void;
}> = ({
  title,
  message,
  warning,
  confirmLabel,
  loading = false,
  onAccept,
  onCancel,
  onCloseAutoFocus,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);
  return (
    <Dialog open onOpenChange={open => !open && !loading && onCancel()}>
      <DialogContent
        className='sm:max-w-[425px]'
        onOpenAutoFocus={e => {
          e.preventDefault();
          confirmRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        {warning && (
          <p className='flex items-start gap-2 text-sm text-amber-800'>
            <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-500' />
            {warning}
          </p>
        )}
        <DialogFooter>
          <Button variant='outline' onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button ref={confirmRef} onClick={onAccept} disabled={loading}>
            {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
